import { api } from '@/services/api/AxiosBaseService'

/**
 * Tabela de precificação da proposta — versionada, com uma só versão ATIVA.
 *
 * Reajuste de preço não edita o que está no ar: duplica-se a versão ativa,
 * ajusta-se o rascunho e ativa-se. Propostas já geradas não mudam porque
 * congelam tudo no snapshot.
 */

export type PricingStatus = 'RASCUNHO' | 'ATIVA' | 'ARQUIVADA'

export interface PricingTableSummary {
  id: string
  version: number
  name: string
  status: PricingStatus
  notes: string | null
  activatedAt: string | null
  activatedBy: string | null
  createdBy: string | null
  counts: { prices: number; weights: number; bands: number }
}

export interface PricingPriceRow {
  id: string
  tierId: string
  tierName: string
  category: string
  fullPrice: number
}

export interface PricingWeightRow {
  id: string
  tierId: string
  tierName: string
  category: string
  itemId: string
  itemName: string
  weight: number
}

export interface PricingBandRow {
  id: string
  curveId: string
  curveName: string
  minLives: number
  maxLives: number | null
  percent: number
}

export interface PricingTableDetail {
  id: string
  version: number
  name: string
  status: PricingStatus
  notes: string | null
  editable: boolean
  /** Impedimentos para ativar (ex.: pesos que não somam 1). Vazio = pode ativar. */
  problems: string[]
  curves: { id: string; name: string }[]
  prices: PricingPriceRow[]
  weights: PricingWeightRow[]
  bands: PricingBandRow[]
}

const BASE = '/settings/commercial/proposal-pricing'

export const ProposalPricingService = {
  async list(): Promise<PricingTableSummary[]> {
    const r = await api.get<{ data: PricingTableSummary[] }>(BASE)
    return r.data.data
  },

  async show(id: string): Promise<PricingTableDetail> {
    const r = await api.get<{ data: PricingTableDetail }>(`${BASE}/${id}`)
    return r.data.data
  },

  async duplicate(id: string, name: string): Promise<{ id: string; version: number }> {
    const r = await api.post<{ data: { id: string; version: number } }>(`${BASE}/${id}/duplicate`, { name })
    return r.data.data
  },

  async activate(id: string): Promise<void> {
    await api.post(`${BASE}/${id}/activate`)
  },

  async rename(id: string, payload: { name: string; notes?: string | null }): Promise<void> {
    await api.put(`${BASE}/${id}`, payload)
  },

  /** Salva preços, pesos e faixas de uma vez e devolve os problemas restantes. */
  async saveContent(id: string, payload: {
    prices?: { tierId: string; fullPrice: number }[]
    weights?: { tierId: string; itemId: string; weight: number }[]
    bands?: { curveId: string; minLives: number; maxLives: number | null; percent: number }[]
  }): Promise<string[]> {
    const r = await api.put<{ data: { problems: string[] } }>(`${BASE}/${id}/content`, payload)
    return r.data.data.problems
  },

  async destroy(id: string): Promise<void> {
    await api.delete(`${BASE}/${id}`)
  },
}

// ----------------------- Importar planilha -----------------------
export interface ImportOption {
  action: string
  label: string
  /** Alternativa menos provável — fica recolhida na tela. */
  secondary?: boolean
}

export interface ImportDecision {
  key: string
  kind: 'line' | 'tier' | 'item' | 'missing_tier'
  title: string
  detail: string
  options: ImportOption[]
  suggested: string
}

export interface ImportPreview {
  token: string
  fileName: string
  sheets: string[]
  summary: { lines: number; tiers: number; priceChanges: number; weightChanges: number; decisions: number }
  activeVersion: { version: number; name: string } | null
  prices: { line: string; tier: string; renamedFrom: string | null; current: number | null; next: number }[]
  decisions: ImportDecision[]
  warnings: string[]
}

export const ProposalPricingImportService = {
  async preview(file: File): Promise<ImportPreview> {
    const fd = new FormData()
    fd.append('file', file)
    const r = await api.post<{ data: ImportPreview }>('/settings/commercial/proposal-pricing/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return r.data.data
  },
  async apply(payload: { token: string; name: string; decisions: Record<string, string>; activate: boolean }): Promise<{ id: string; version: number; status: string }> {
    const r = await api.post<{ data: { id: string; version: number; status: string } }>(
      '/settings/commercial/proposal-pricing/import/apply',
      // Lista, não mapa: as chaves das decisões trazem ids e não podem passar
      // pela conversão de chaves da API.
      {
        token: payload.token, name: payload.name, activate: payload.activate,
        decisions: Object.entries(payload.decisions).map(([key, action]) => ({ key, action })),
      },
    )
    return r.data.data
  },
}
